import { ThunkDispatch } from 'redux-thunk';
import { Modal } from 'antd';
import { outboundLink } from 'react-ga';

import { SiteChangeOptions, SiteChangeSource } from '../types/SiteChangeTypes';
import { IApplicationState } from '../store';
import { actionCreators as SessionActions, ILoadOpenDatesParams } from '../store/Sessions';
import { actionCreators as SiteActions } from '../store/Sites';
import { actionCreators as BookingActions } from '../store/Booking';
import { actionCreators as GiftVoucherActions } from '../store/GiftVoucher';
import { actionCreators as PlayerActions } from '../store/Players';
import ISite from '../interfaces/ISite';
import { PurchaseTypes } from '../store/PurchaseTypes';
import { Routes, navigateTo } from '../helpers/routes';
import Logging from '../service/logging';
import BrazeEvents from '../helpers/braze';

/**
 * Centralized service for handling site/venue changes
 * Encapsulates all business logic for site selection operations
 * NOTE this is not an api interface like the other service files in this folder, 
 * It's just for interacting with the redux store data
 */
export class SiteChangeService {
    /**
     * Changes the selected site with comprehensive business logic handling
     * @param site The site to change to (undefined to clear selection)
     * @param nearest Whether this site was selected as the nearest location
     * @param nearby Whether this site was selected from a nearby venue widget
     * @param dispatch Redux dispatch function
     * @param getState Function to get current Redux state
     * @param options Configuration options for the change operation
     */
    static async changeSite(
        site: ISite | undefined,
        nearest: boolean,
        nearby: boolean = false,
        locationWidget: boolean = false,
        dispatch: ThunkDispatch<IApplicationState, any, any>,
        getState: () => IApplicationState,
        options: SiteChangeOptions
    ): Promise<void> {

        // Handle site clearing
        if (!site) {
            return this.clearSite(dispatch, options, getState);
        }

        // Clear existing state if requested
        if (options.clearBooking !== false) {
            dispatch(SessionActions.clearSelectedDateTime());
            dispatch(BookingActions.clearBooking());
        }

        if (options.clearVoucher !== false) {
            dispatch(GiftVoucherActions.clearGiftVoucher());
        }

        // Select the new site
        dispatch(SiteActions.siteSelected(site, nearest, nearby, locationWidget));

        // Load gift voucher config for the new site
        dispatch(GiftVoucherActions.getGiftVoucherConfig(false, site.siteId));

        //clear the selected currency (which currently can only come from the global gift vouchers page
        dispatch(GiftVoucherActions.selectVoucherCurrency(undefined));

        // Handle analytics tracking
        if (options.trackAnalytics !== false) {
            this.trackSiteChange(site, options.source);
        }

        // Set global logging context
        Logging.SetGlobalSiteKey(site.code);

        // Handle discount code clearing
        if (!options.skipCodeClearing) {
            await this.handleCodeClearing(site, dispatch, getState);
        }

        // Handle external booking URLs. A venue unlocked via a preview access code runs the in-app flow
        // instead of redirecting to its 3rd-party booking system, so staff can test our experience.
        if (site.useExternalBookingUrl && !site.previewAccess && !options.skipModalDialogs) {
            return this.handleExternalBooking(site, dispatch, getState, options);
        }

        // Handle navigation
        if (options.handleNavigation !== false) {
            this.handleNavigation(site, dispatch, getState);
        }

        // Load open dates if requested
        if (options.loadOpenDates !== false) {
            await this.loadOpenDates(site, dispatch, getState);
        }
    }

    /**
     * Clears the selected site
     */
    static async clearSite(
        dispatch: ThunkDispatch<IApplicationState, any, any>,
        options: SiteChangeOptions,
        getState: () => IApplicationState,
    ): Promise<void> {
        console.log(`SiteChangeService.clearSite called, source: ${options.source}`);

        if (options.clearBooking !== false) {
            dispatch(SessionActions.clearSelectedDateTime());
            dispatch(BookingActions.clearBooking());
        }

        if (options.clearVoucher !== false) {
            dispatch(GiftVoucherActions.clearGiftVoucher());
        }

        dispatch(SiteActions.siteSelected(undefined, false));
        Logging.SetGlobalSiteKey('');

        this.handleNavigation(undefined, dispatch, getState);
    }

    /**
     * Handles analytics tracking for site changes
     */
    private static trackSiteChange(site: ISite, source: SiteChangeSource): void {
        // Track with Braze
        if (source === SiteChangeSource.LocationBased) {
            // Could add a separate event for auto-selection if needed
            BrazeEvents.VenueSelected(site);
        } else {
            BrazeEvents.VenueSelected(site);
        }
    }

    /**
     * Handles discount code clearing logic
     */
    private static async handleCodeClearing(
        site: ISite,
        dispatch: ThunkDispatch<IApplicationState, any, any>,
        getState: () => IApplicationState
    ): Promise<void> {
        const state = getState();
        const appliedCodes = state.booking.appliedCodes;
        const preSelectedCode = state.booking.preSelectedCode;
        const preSelectedSite = state.sites.preselectedSite;

        // Clear codes if any are applied or if preselected code exists but site doesn't match
        if (
            appliedCodes.length > 0 ||
            (preSelectedCode &&
                preSelectedSite &&
                site.urlKey !== preSelectedSite &&
                site.siteId !== parseInt(preSelectedSite))
        ) {
            dispatch(BookingActions.clearCode());
        }
    }

    /**
     * Handles external booking URL flow with modal dialog
     */
    private static async handleExternalBooking(
        site: ISite,
        dispatch: ThunkDispatch<IApplicationState, any, any>,
        getState: () => IApplicationState,
        options: SiteChangeOptions
    ): Promise<void> {
        const state = getState();
        
        // Clear players for external booking
        dispatch(PlayerActions.clearPlayers());

        // Get UTM parameters for external URL
        const utm_id = state.attribution.utm_Id;
        const utm_campaign = state.attribution.utm_campaign;
        const utm_content = state.attribution.utm_content;
        const utm_medium = state.attribution.utm_medium;
        const utm_source = state.attribution.utm_source;
        const utm_term = state.attribution.utm_term;
        const preSelectedCode = state.booking.preSelectedCode;

        const modal = Modal.confirm({
            icon: undefined,
            centered: true,
            maskClosable: true,
            okCancel: true,
            autoFocusButton: null,
            width: 'auto',
            cancelText: 'Cancel', // TODO: Use translation
            cancelButtonProps: {
                type: 'link',
                className: 'cancel',
            },
            okType: 'default',
            okText: 'Continue', // TODO: Use translation
            title: 'External Booking', // TODO: Use translation with JSX
            className: 'sites-modal-container',
            content: 'You will be redirected to an external booking site.', // TODO: Use translation with JSX
            onOk: () => {
                outboundLink(
                    { label: 'Redirecting to External Site' },
                    () => {
                        window.location.href = SiteChangeService.addUtmToUrl(
                            site.externalBookingUrl,
                            utm_id,
                            utm_campaign,
                            utm_content,
                            utm_medium,
                            utm_source,
                            utm_term,
                            preSelectedCode,
                            !site.excludeExternalBookingDecoration
                        );
                    },
                    ['zerolatency']
                );
            },
            onCancel: () => {
                modal.destroy();
            },
        });

        // Still handle navigation for external sites
        if (options.handleNavigation !== false) {
            this.handleNavigation(site, dispatch, getState);
        }
    }

    /**
     * Handles navigation/routing after site selection
     */
    private static handleNavigation(
        site: ISite | undefined,
        dispatch: ThunkDispatch<IApplicationState, any, any>,
        getState: () => IApplicationState
    ): void {
        const state = getState();
        const selectedPurchaseType = state.purchaseType.selectedPurchaseType;

        // Get current query string to preserve across navigation
        // (navigateTo appends the access code itself, for every route in the funnel)
        const rawQueryString = window.location.search;
        if (site) {
            if (selectedPurchaseType === PurchaseTypes.Booking) {
                dispatch(navigateTo(Routes.BookNow, site.urlKey, rawQueryString));
            } else if (selectedPurchaseType === PurchaseTypes.GiftVoucher) {
                dispatch(navigateTo(Routes.GiftVoucher, site.urlKey, rawQueryString));
            } else if (selectedPurchaseType === PurchaseTypes.PrivateEvent) {
                dispatch(navigateTo(Routes.PrivateEvent, site.urlKey, rawQueryString));
            }
        }
        else {
            if (selectedPurchaseType === PurchaseTypes.Booking) {
                //venue is cleared, but the access code stays in the url so the hidden venue
                //remains selectable (and survives a refresh from here)
                dispatch(navigateTo(Routes.BookNow, null, rawQueryString));
            } else if (selectedPurchaseType === PurchaseTypes.GiftVoucher) {
                dispatch(navigateTo(Routes.GiftVoucher, null, rawQueryString));
            } else if (selectedPurchaseType === PurchaseTypes.PrivateEvent) {
                dispatch(navigateTo(Routes.PrivateEvent, null, rawQueryString));
            }
        }
    }

    /**
     * Loads open dates for the selected site
     */
    private static async loadOpenDates(
        site: ISite,
        dispatch: ThunkDispatch<IApplicationState, any, any>,
        getState: () => IApplicationState
    ): Promise<void> {
        const state = getState();
        const selectedGame = state.games.selectedGame;
        const selectedPlayers = state.players.selectedPlayers;
        const accessCode = state.sites.accessCode;
        const selectedPurchaseType = state.purchaseType.selectedPurchaseType;

        const packageTypeId = selectedPurchaseType === PurchaseTypes.Booking ? 1 : 2;
        const highlightPriceEnabled = state.sites.bookingOptions?.usePriceTierHighlighting ?? false;
        const priceBucketsEnabled = highlightPriceEnabled && selectedPurchaseType === PurchaseTypes.Booking;

        dispatch(
            SessionActions.loadOpenDates({
                siteId: site.siteId,
                accessCode: accessCode,
                experienceId: selectedGame?.experienceId,
                packageTypeId: packageTypeId,
                playerCount: priceBucketsEnabled ? selectedPlayers : undefined
            } as ILoadOpenDatesParams)
        );
    }

    /**
     * Adds UTM parameters to external booking URLs
     */
    private static addUtmToUrl(
        url: string,
        id: string | undefined,
        campaign: string | undefined,
        content: string | undefined,
        medium: string | undefined,
        source: string | undefined,
        term: string | undefined,
        promocode: string | undefined,
        addTrackId: boolean,
    ): string {
        try {
            const urlObject = new URL(url);
            const params = urlObject.searchParams;

            if (addTrackId) {
                params.set('externalTrackId', encodeURIComponent('G-KG3RN78W7H'));
            }

            if (id) params.set('utm_id', id);
            if (campaign) params.set('utm_campaign', campaign);
            if (content) params.set('utm_content', content);
            if (medium) params.set('utm_medium', medium);
            if (source) params.set('utm_source', source);
            if (term) params.set('utm_term', term);
            if (promocode) params.set('promo', promocode);

            return urlObject.toString();
        } catch (error) {
            console.error('Error adding UTM parameters to URL:', error);
            return url;
        }
    }
}
